const MAC_REGEX = /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$|^([0-9A-Fa-f]{2}-){5}[0-9A-Fa-f]{2}$|^[0-9A-Fa-f]{12}$/;

/**
 * Parses a MAC address string into an array of 6 bytes.
 * Accepts formats: `AA:BB:CC:DD:EE:FF`, `AA-BB-CC-DD-EE-FF`, `AABBCCDDEEFF`.
 *
 * @param macAddress The MAC address string to parse.
 * @returns An array of 6 bytes (numbers 0-255).
 * @throws {Error} If the MAC address format is invalid.
 */
export function parseMacAddress(macAddress: string): number[] {
	if (!macAddress || !MAC_REGEX.test(macAddress)) {
		throw new Error(`Invalid MAC address: "${macAddress}". Expected format: AA:BB:CC:DD:EE:FF, AA-BB-CC-DD-EE-FF, or AABBCCDDEEFF`);
	}

	const hex = macAddress.replace(/[:-]/g, '');
	const bytes: number[] = [];
	for (let i = 0; i < 12; i += 2) {
		bytes.push(parseInt(hex.substring(i, i + 2), 16));
	}

	return bytes;
}

const MAGIC_PACKET_HEADER = [0xff, 0xff, 0xff, 0xff, 0xff, 0xff];
const MAC_REPETITIONS = 16;

/**
 * Creates a Wake-on-LAN magic packet for the given MAC address.
 * The packet is 102 bytes: 6 bytes of 0xFF followed by the MAC address repeated 16 times.
 *
 * @param macAddress The target MAC address (formats: `AA:BB:CC:DD:EE:FF`, `AA-BB-CC-DD-EE-FF`, `AABBCCDDEEFF`).
 * @returns The magic packet as a number array (102 bytes, each 0-255).
 * @throws {Error} If the MAC address format is invalid.
 */
export function createMagicPacket(macAddress: string): number[] {
	const macBytes = parseMacAddress(macAddress);

	const packet: number[] = [...MAGIC_PACKET_HEADER];
	for (let i = 0; i < MAC_REPETITIONS; i++) {
		packet.push(...macBytes);
	}

	return packet;
}
